import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PermissionDef } from "@gaole/shared";
import { api } from "../lib/api";
import { toast } from "../stores/toast";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { Badge, Button, Input, useConfirm } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

interface RoleRow {
  id: string;
  name: string;
  label: string;
  isSystem: boolean;
  userCount: number;
  permissions: string[];
}

interface RoleForm {
  name: string;
  label: string;
  permissions: string[];
}

function emptyForm(): RoleForm {
  return { name: "", label: "", permissions: [] };
}

function PermissionMatrix({
  permissions,
  selected,
  onChange,
  disabled,
}: {
  permissions: PermissionDef[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}) {
  const byModule = useMemo(() => {
    const map = new Map<string, PermissionDef[]>();
    for (const p of permissions) {
      const list = map.get(p.module) ?? [];
      list.push(p);
      map.set(p.module, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  const toggle = (key: string) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  const toggleModule = (modulePerms: PermissionDef[], allSelected: boolean) => {
    if (disabled) return;
    const next = new Set(selected);
    for (const p of modulePerms) {
      if (allSelected) next.delete(p.key);
      else next.add(p.key);
    }
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {byModule.map(([module, perms]) => {
        const allSelected = perms.every((p) => selected.has(p.key));
        return (
          <div key={module} className="rounded-xl border border-line bg-white/[0.02] p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/50">{module}</p>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggleModule(perms, allSelected)}
                  className="text-[10px] font-semibold text-brand-pink hover:underline"
                >
                  {allSelected ? "Clear module" : "Select all"}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {perms.map((p) => (
                <label key={p.key} className="flex cursor-pointer items-start gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(p.key)}
                    disabled={disabled}
                    onChange={() => toggle(p.key)}
                    className="mt-0.5 accent-brand-pink"
                  />
                  <span>
                    <span className="block text-white/90">{p.label.split(": ")[1] ?? p.label}</span>
                    <span className="font-mono text-[10px] text-white/35">{p.key}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Roles() {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [form, setForm] = useState<RoleForm>(emptyForm());
  const [permSet, setPermSet] = useState<Set<string>>(new Set());

  const { data: roles, isLoading } = useQuery({
    queryKey: ["rbac", "roles"],
    queryFn: () => api<{ roles: RoleRow[] }>("/rbac/roles").then((r) => r.roles),
  });

  const { data: permissions } = useQuery({
    queryKey: ["rbac", "permissions"],
    queryFn: () => api<{ permissions: PermissionDef[] }>("/rbac/permissions").then((r) => r.permissions),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name,
        label: form.label,
        permissions: [...permSet],
      };
      if (editing) {
        await api(`/rbac/roles/${editing.id}`, {
          method: "PUT",
          body: { label: body.label, permissions: body.permissions },
        });
      } else {
        await api("/rbac/roles", { method: "POST", body });
      }
    },
    onSuccess: async () => {
      toast("success", editing ? "Role updated" : "Role created");
      setPanelOpen(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["rbac", "roles"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/rbac/roles/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      toast("success", "Role deleted");
      await queryClient.invalidateQueries({ queryKey: ["rbac", "roles"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setPermSet(new Set());
    setPanelOpen(true);
  };

  const openEdit = (role: RoleRow) => {
    setEditing(role);
    setForm({ name: role.name, label: role.label, permissions: role.permissions });
    setPermSet(new Set(role.permissions));
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setEditing(null);
  };

  useBottomBarActions(() => {
    if (panelOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closePanel },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled: saveMut.isPending || (!editing && (!form.name || !form.label)),
          onClick: () => saveMut.mutate(),
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  const isSuperadmin = editing?.name === "superadmin";
  const readOnly = isSuperadmin;

  const columns: Column<RoleRow>[] = [
    {
      key: "label",
      header: "Role",
      sortable: true,
      sortValue: (r) => r.label,
      exportValue: (r) => `${r.label} (${r.name})`,
      render: (r) => (
        <div>
          <p className="font-medium">{r.label}</p>
          <p className="font-mono text-[10px] text-white/40">{r.name}</p>
        </div>
      ),
    },
    {
      key: "users",
      header: "Users",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.userCount,
      render: (r) => <span className="tabular-nums">{r.userCount}</span>,
    },
    {
      key: "perms",
      header: "Permissions",
      hideSm: true,
      sortable: true,
      sortValue: (r) => (r.name === "superadmin" ? Number.MAX_SAFE_INTEGER : r.permissions.length),
      exportValue: (r) => (r.name === "superadmin" ? "All permissions" : String(r.permissions.length)),
      render: (r) =>
        r.name === "superadmin" ? (
          <Badge color="pink">All permissions</Badge>
        ) : (
          <span className="text-white/60">{r.permissions.length} granted</span>
        ),
    },
    {
      key: "system",
      header: "Type",
      hideSm: true,
      sortable: true,
      sortValue: (r) => (r.isSystem ? "System" : "Custom"),
      exportValue: (r) => (r.isSystem ? "System" : "Custom"),
      render: (r) => (r.isSystem ? <Badge color="blue">System</Badge> : <Badge color="gray">Custom</Badge>),
    },
  ];

  return (
    <>
      <ListPageShell>
        <div>
          <h1 className="font-display text-lg font-bold">Roles & Access</h1>
          <p className="mt-0.5 text-xs text-white/50">Define staff roles and permission bundles.</p>
        </div>

        <DataTable
          columns={columns}
          rows={roles}
          rowKey={(r) => r.id}
          loading={isLoading}
          exportFilename="roles"
          onRowClick={(r) => openEdit(r)}
          empty={{ title: "No roles yet", hint: "Create a custom role to get started." }}
        />
      </ListPageShell>

      <RightSidebarPanel
        open={panelOpen}
        title={editing ? `Edit ${editing.label}` : "Create role"}
        subtitle={readOnly ? "Superadmin has implicit full access and cannot be edited." : undefined}
        onClose={() => setPanelOpen(false)}
        wide
        footer={
          !readOnly && (
            <div className="flex justify-end gap-2">
              {editing && !editing.isSystem && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() =>
                    confirm({
                      title: "Delete role?",
                      message: `Remove "${editing.label}"? Users with this role will lose its permissions.`,
                      confirmLabel: "Delete",
                      danger: true,
                      action: () => deleteMut.mutate(editing.id),
                    })
                  }
                >
                  Delete
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setPanelOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={saveMut.isPending || (!editing && (!form.name || !form.label))}
                onClick={() => saveMut.mutate()}
              >
                Save
              </Button>
            </div>
          )
        }
      >
        {readOnly ? (
          permissions && (
            <PermissionMatrix
              permissions={permissions}
              selected={new Set(permissions.map((p) => p.key))}
              onChange={() => {}}
              disabled
            />
          )
        ) : (
          <div className="sidebar-form">
            {!editing && (
              <Input
                label="Internal name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toLowerCase() }))}
                placeholder="warehouse_lead"
                hint="Lowercase letters, numbers, hyphens"
              />
            )}
            <Input
              label="Display label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="Warehouse lead"
            />
            {permissions && (
              <PermissionMatrix permissions={permissions} selected={permSet} onChange={setPermSet} />
            )}
          </div>
        )}
      </RightSidebarPanel>

      {dialog}
    </>
  );
}
